import type React from 'react';
import { useCallback, useState } from 'react';
import type { ConnectedProps } from 'react-redux';

import type { AssetsRelationSchema } from '@iconik-io/iconik-api/src/services/assets';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import ControlsModal from '../../../ui/controls_modal/controls_modal';
import CheckboxField from '../../../ui/forms/field_wrappers/checkbox/checkbox';
import SelectFieldWrapper from '../../../ui/forms/field_wrappers/select/select';
import InputField from '../../../ui/forms/field_wrappers/text/text';
import { ui } from '../../../utils/helpers';
import withContextItems from '../context_items_hoc';
import type {
  ContextItemsHocProps,
  ModalComponentProps,
} from '../modals_manager/types';
import connector from './connector';
import CreateRelationSearch from './create_relation_search/create_relation_search';

import styles from './create_relations.module.css';

type CreateRelationFormData = {
  relation_type?: string;
  description?: string;
  invert: boolean;
};

const defaultLabels = {
  destination_label: '',
  source_label: '',
};

type Props = ModalComponentProps &
  ContextItemsHocProps &
  ConnectedProps<typeof connector>;

export const CreateRelationModal: React.FC<Props> = (props) => {
  const relationTypeDefault = props.relationsOptions[0]
    ? props.relationsOptions[0].value
    : undefined;

  const [formData, setFormData] = useState<Partial<CreateRelationFormData>>({
    relation_type: relationTypeDefault,
    description: '',
    invert: false,
  });
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [search, setSearch] = useState<SearchItemEntity[]>([]);

  const onCancel = (): void => {
    props.onCancel();
  };

  const getTableItem = (id: string) =>
    search.find((searchItem) => searchItem?.id === id);

  const getItemData = (
    id: string,
    form: CreateRelationFormData,
  ): AssetsRelationSchema | undefined => {
    if (!form.relation_type) {
      return undefined;
    }
    return {
      related_to_asset_id: form.invert ? props.item.id : id,
      relation_type: form.relation_type,
      description: form.description ? form.description : undefined,
    };
  };

  const getSource = (invert: boolean, id: string): string | undefined =>
    invert ? id : props.item?.id;

  const getItemsToSubmit = (
    ids: string[],
    form: CreateRelationFormData,
  ): Promise<unknown>[] => {
    const items: Promise<unknown>[] = [];
    ids.forEach((id) => {
      const data = getItemData(id, form);
      const source = getSource(form.invert, id);
      const tableItem = getTableItem(id);

      if (data && source && tableItem) {
        items.push(props.assetRelationsCreate(source, data, tableItem));
      }
    });
    return items;
  };

  const onConfirm = (form: CreateRelationFormData): Promise<unknown> => {
    const ids = selectedAssets;
    return Promise.all(getItemsToSubmit(ids, form)).then(props.onConfirm);
  };

  const onChange = (data: CreateRelationFormData): void => {
    setFormData({ ...formData, ...data });
  };

  const onSearch = useCallback((searchItems: SearchItemEntity[]): void => {
    setSearch(searchItems);
    setSelectedAssets([]);
  }, []);

  const onSelect = useCallback(
    (id: string, value: boolean): void => {
      if (value) {
        if (!selectedAssets.includes(id)) {
          const selectedValue = [...selectedAssets, id];

          setSelectedAssets(selectedValue);
        }
      } else {
        setSelectedAssets(selectedAssets.filter((asset) => asset !== id));
      }
    },
    [selectedAssets],
  );

  const { isOpen, labels, relationsOptions } = props;

  let isDirectional = true;
  const relationType = formData.relation_type;
  if (formData && relationType && relationsOptions) {
    const currentRelation = relationsOptions.find(
      (option) => option.value === relationType,
    );
    isDirectional = Boolean(currentRelation && currentRelation.is_directional);
  }

  const item = props.item;

  const relationTypeLabel = relationType ? labels[relationType] : defaultLabels;

  return (
    <ControlsModal
      isOpen={isOpen}
      headerText="Create relation"
      onCancel={onCancel}
      onConfirm={onConfirm}
      confirmButtonText="Relate"
      variant="success"
      isForm
      onChange={onChange}
    >
      <div className={styles.form}>
        <div className={styles.form_block}>
          <SelectFieldWrapper
            label="Relation type"
            name="relation_type"
            options={relationsOptions}
            value={relationType}
          />
        </div>
        {isDirectional && (
          <div className={styles.form_block}>
            <CheckboxField name="invert" label="Reverse" />
          </div>
        )}
        <p className={styles.relation} {...ui('label')}>
          {'Relate as '}
          {formData.invert
            ? relationTypeLabel?.destination_label
            : relationTypeLabel?.source_label}
          {':'}
        </p>
        <CreateRelationSearch
          id={item.id}
          onSearch={onSearch}
          onSelect={onSelect}
          selectedItems={selectedAssets}
        />
        <InputField name="description" label="Description" />
      </div>
    </ControlsModal>
  );
};

export default withContextItems(connector(CreateRelationModal));
